import { create } from "zustand";
import type { CreateDataObjectTypesModelWithTranslationsReturn } from "@certa/queries/types/dataObjectsAPIs.types";
import type { DataObjectUISchemaRaw } from "@certa/types/src/dataObjects.types";

type DataObjectsState = {
  // State
  objectTypes: CreateDataObjectTypesModelWithTranslationsReturn;
  translationsByObjectType: Record<string, DataObjectUISchemaRaw>;
  accessibleFieldsByObjectType: Record<string, string[]>;

  // Setters
  setObjectTypes: (
    types: CreateDataObjectTypesModelWithTranslationsReturn
  ) => void;
  setTranslation: (
    objectTypeTag: string,
    translation: DataObjectUISchemaRaw
  ) => void;
  setTranslations: (
    translations: { tag: string; translatedUiSchema: DataObjectUISchemaRaw }[]
  ) => void;
  setAccessibleFields: (objectTypeTag: string, fields: string[]) => void;

  // Getters
  getObjectTypeByTag: (
    tag: string
  ) => CreateDataObjectTypesModelWithTranslationsReturn[number] | undefined;
  getTranslatedUISchema: (
    objectTypeTag: string | undefined
  ) => DataObjectUISchemaRaw | undefined;
  getAccessibleFields: (objectTypeTag: string | undefined) => string[];

  // Reset
  reset: () => void;
};

const initialState = {
  objectTypes: [],
  translationsByObjectType: {},
  accessibleFieldsByObjectType: {}
};

export const useDataObjectsStore = create<DataObjectsState>()((set, get) => ({
  ...initialState,

  setObjectTypes: types => {
    set({ objectTypes: types });
  },

  setTranslation: (objectTypeTag, translation) => {
    set(state => ({
      translationsByObjectType: {
        ...state.translationsByObjectType,
        [objectTypeTag]: translation
      }
    }));
  },

  setTranslations: translations => {
    const translationsMap = translations.reduce(
      (acc, { tag, translatedUiSchema }) => {
        acc[tag] = translatedUiSchema;
        return acc;
      },
      {} as Record<string, DataObjectUISchemaRaw>
    );

    set({ translationsByObjectType: translationsMap });
  },

  setAccessibleFields: (objectTypeTag, fields) => {
    set(state => ({
      accessibleFieldsByObjectType: {
        ...state.accessibleFieldsByObjectType,
        [objectTypeTag]: fields
      }
    }));
  },

  getObjectTypeByTag: tag => {
    return get().objectTypes.find(type => type.tag === tag);
  },

  getTranslatedUISchema: (objectTypeTag: string | undefined) => {
    if (!objectTypeTag) return undefined;
    return get().translationsByObjectType[objectTypeTag];
  },

  getAccessibleFields: (objectTypeTag: string | undefined) => {
    if (!objectTypeTag) return [];
    const accessibleFields = get().accessibleFieldsByObjectType[objectTypeTag];
    return accessibleFields || [];
  },

  reset: () => {
    set(initialState);
  }
}));
